
import { HeaderData } from "whocan-lib";

import { TemplateKey } from "./template-keys"
export { TemplateKey as TemplateKey };

export type ThemeType = 'invitation' | 'generic' | null;
type TemplateConfig = {
    general?: {
        inheritFrom?: string;
        icon?: string;
        previewImage?: string;
        formToUse?: string,
        defaultOptions?: string,
        useStepper?: boolean,
        useOptionsListPreview?: boolean,

    };

    availableSettings?: {
        pollDescription?: boolean;
        longDescription?: boolean;
        when?: boolean;
        whenAdvanced?: boolean,
        locationFirstStep?: boolean;
        timeZoneAdvanced?: boolean,
        location?: boolean;
        locationAdvanced?: boolean,
        /**
         * Meeting link (header.meetingLink, calendar quick wins C, K1 = A): the same value as the location flag of the
         * same place - field in the first step, field in the main settings, toggle showMeetingLinkAdvanced in the
         * advanced section. Exception (C2, Kai 2026-11-02): invitation, potluck and duties and every template inheriting
         * from them offer no meeting link - all three false, set explicitly in the base templates. The spec
         * template-setting-flags.spec.ts checks the pairs and the exception for every template.
         */
        meetingLinkFirstStep?: boolean;
        meetingLink?: boolean;
        meetingLinkAdvanced?: boolean,
        advanced?: boolean
        respondTill?: boolean;
        respondTillAdvanced?: boolean;
        chooseTheme?: boolean;
        /** group "register people" incl. its subsettings (askForMultiplePersons → askForKids; askForVegetarians); first step */
        registerPeople?: boolean;
        /** same group in the advanced section */
        registerPeopleAdvanced?: boolean;
        /**
         * subsetting of registerPeople: hard limit for the whole event (header.maxPersons), shown as the
         * last child of the register-people tree wherever that tree is shown (first step, advanced
         * section, edit-header dialog). Inherited along inheritFrom; participation opts out explicitly -
         * there the per-option slot limit applies (workorder-header-max-persons-2026-09-10, decision 7).
         */
        maxPersons?: boolean;
        privateComments?: boolean;
        askForEmailAdress?: boolean;
        /** subsetting of askForEmailAdress — confirmation mail to the participant; only the advanced/participation/invitation/potluck families */
        sendVoteConfirmation?: boolean;
        askForName?: boolean;
        askForPhoneNumber?: boolean;
        hideParticipants?: boolean; /**depricated */
        showParticipants?: boolean;
        getNotifications?: boolean;
        dateRelated?: boolean;
        allowToAddOptions?: boolean;
        askForPublicComments?: boolean;
        allowParticipantsNotifications?: boolean;
        mayBe?: boolean;
        allowMayBeAdvanced?: boolean;
        allowToAddOptionsAdvanced?: boolean,
        formToUseAdvanced?: boolean
    }

    defaultSettings?: HeaderData //  -> be aware that single attributes of the default settings are not inherited, the whole defaultSettings object need to be configured per template
    /**
     * Defaults that only apply when the poll owner is registered (auth account with email) —
     * for settings that are locked for anonymous owners, e.g. sendVoteConfirmation. Applied by
     * HeaderService.initNewHeader() on top of defaultSettings; resolved per attribute via
     * getConfigAttribute(), so it IS inherited along inheritFrom (unlike defaultSettings).
     */
    defaultSettingsIfRegistered?: HeaderData
    options?: {
        skipOptionsStep?: boolean;
        showAddDate?: boolean;
        showDescription?: boolean;
        showMaxParticipants?: boolean;
        optionTypes?: string[],
        showOptionTypeIcon?: boolean
    };
    responsesDashboard?: {
        showChartNumberOfReponses?: boolean;
        showDaysSinceCreation?: boolean;
        showChartBestDateOptions?: boolean;
        showChartBestTextOptions?: boolean;
        showChartRegisterPeople?: boolean;
    };
}

/** Defines which templates are visible and in what order they appear in the UI.
 * Reihenfolge gemaess Blog-Series (`poll-types.md` + Spokes): spezialisierte
 * Templates zuerst, Advanced als Catch-all ans Ende. Source-of-Truth fuer
 * Blog/Website-Texte ist diese Liste — Aenderungen hier muessen in den
 * korrespondierenden Blog-Posten nachgezogen werden. */
export const templateDisplayOrder: string[] = [
    TemplateKey.schedule,
    TemplateKey.invitation,
    TemplateKey.potluck,
    TemplateKey.doodle,
    TemplateKey.participation,
    TemplateKey.preference,
    TemplateKey.duties,
    TemplateKey.order,
    TemplateKey.advanced,
];


export const templateConfigMap = new Map<string, TemplateConfig>([

    [TemplateKey.advancedNew, {
        general: {
            inheritFrom: TemplateKey.advanced,
        }
        , defaultSettings: {
            theme: 'no-theme',
            showAdvancedSettings: true,
            showParticipants: true,
        }
    },],
    [TemplateKey.schedule, {
        general: {
            inheritFrom: TemplateKey.advanced,
            icon: 'date_range',
            previewImage: '/poll-preview/calendar.webp',
            useOptionsListPreview: false,
        },
        availableSettings: {
            sendVoteConfirmation: false, // participant confirmation mail only for advanced/participation/invitation/potluck
            registerPeople: false,
            registerPeopleAdvanced: false,
            chooseTheme: true,
            location: false,
            meetingLink: false,
            locationAdvanced: true,
            meetingLinkAdvanced: true,
            when: false,
            whenAdvanced: false,
            respondTillAdvanced: true,
            privateComments: true,
            formToUseAdvanced: true
        },
        defaultSettings: {
            theme: 'calendar',
            showParticipants: true,
            mayBe: true,
        },
        options: {
            showDescription: true,
            showMaxParticipants: true,
            optionTypes: ['date'],
            showOptionTypeIcon: false,
        },
    },],
    [TemplateKey.scheduleNew, {
        general: {
            inheritFrom: TemplateKey.schedule,
        },
        defaultSettings: {
            theme: 'calendar',
            showParticipants: true,
        }
    },],
    [TemplateKey.when2meet, {
        general: {
            inheritFrom: TemplateKey.schedule,
        },
        defaultSettings: {
            theme: 'calendar',
            showParticipants: true,
        }
    },],
    [TemplateKey.invitation, {
        general: {
            inheritFrom: TemplateKey.advanced,
            icon: 'mark_email_read',
            previewImage: 'poll-preview/invitation.webp',
            formToUse: 'invitation',
            useStepper: true
        },
        availableSettings: {
            pollDescription: true,
            when: true,
            advanced: true,
            locationFirstStep: true,
            // no meeting link at an invitation - a party is a place (C2, Kai 2026-11-02)
            meetingLinkFirstStep: false,
            meetingLink: false,
            whenAdvanced: false,
            locationAdvanced: false,
            meetingLinkAdvanced: false,
            respondTillAdvanced: true,
            chooseTheme: true,
            registerPeople: true,
            registerPeopleAdvanced: false,
            maxPersons: true,
            getNotifications: true,
            showParticipants: true,
            allowParticipantsNotifications: true,
            dateRelated: false,
            allowToAddOptions: false,
            askForPublicComments: true,
            formToUseAdvanced: true,
            allowMayBeAdvanced: false
        },
        defaultSettings: {
            theme: 'ballons',
            askForPublicComments: true,
            showParticipants: true,
            showWhen: true,
            // new invitations ask "how many?" by default (decision 4, workorder-register-people-unification-2026-09-10)
            registerPeople: true,
            askForMultiplePersons: true,
        },
        options: {
            skipOptionsStep: true,
            showDescription: true,
            showMaxParticipants: true,
            optionTypes: ['text', 'text_text',],
        },
    },],
    [TemplateKey.birthdayInvitation, {
        general: {
            inheritFrom: TemplateKey.invitation,
        },
        defaultSettings: {
            theme: 'ballons',
            showParticipants: true,
            registerPeople: true,
            askForMultiplePersons: true,
        }
    },],
    [TemplateKey.rsvp, {
        general: {
            inheritFrom: TemplateKey.invitation,
        }
    },],
    [TemplateKey.potluck, {
        general: {
            inheritFrom: TemplateKey.advanced,
            icon: 'flatware',
            previewImage: '/poll-preview/potluck.webp',
            defaultOptions: 'potluck',
            useOptionsListPreview: false,
        },
        availableSettings: {
            chooseTheme: true,
            registerPeople: true,
            registerPeopleAdvanced: false,
            maxPersons: true,
            locationFirstStep: true,
            // no meeting link at a potluck - a party is a place (C2, Kai 2026-11-02)
            meetingLinkFirstStep: false,
            meetingLink: false,
            locationAdvanced: false,
            meetingLinkAdvanced: false,
            when: true,
            whenAdvanced: false,
            respondTillAdvanced: false,
            allowToAddOptions: false,
            formToUseAdvanced: false
        },
        defaultSettings: {
            theme: 'potluck',
            allowToAddOptions: false,
            registerPeople: true,
            askForMultiplePersons: true,
            askForKids: false,
            showParticipants: true,
            showWhen: true
        },
        options: {
            optionTypes: ['textAndNumber_textAndNumber'],
            showDescription: true,
            showMaxParticipants: true,
            showOptionTypeIcon: false,
        },
        responsesDashboard: {
            showChartBestDateOptions: false,
            showChartBestTextOptions: false,
        }
    }],
    [TemplateKey.potluckNew, {
        general: {
            inheritFrom: TemplateKey.potluck,
            useStepper: true,
            defaultOptions: ''
        },
        defaultSettings: {
            theme: 'potluck',
            allowToAddOptions: false,
            registerPeople: true,
            askForMultiplePersons: true,
            askForKids: false,
            showParticipants: true,
        },
    },],
    [TemplateKey.participation, {
        general: {
            inheritFrom: TemplateKey.advanced,
            icon: 'how_to_reg',
            previewImage: '/poll-preview/class-booking.webp',
            useOptionsListPreview: false,
        },
        availableSettings: {
            chooseTheme: true,
            registerPeople: false,
            registerPeopleAdvanced: false,
            // no event-wide people limit here: would inherit true from advanced, but class booking
            // limits per option slot (workorder-header-max-persons-2026-09-10, decision 7)
            maxPersons: true,
            location: false,
            meetingLink: false,
            locationAdvanced: true,
            meetingLinkAdvanced: true,
            when: false,
            whenAdvanced: false,
            respondTillAdvanced: true,
            allowToAddOptions: false,
            formToUseAdvanced: false,
            allowMayBeAdvanced: false

        },
        defaultSettings: {
            theme: 'people-register',
            showParticipants: false,
            askForEmailAdress: true, // class booking: participants leave an email by default (decision Kai 2026-09-09)
        },
        defaultSettingsIfRegistered: {
            sendVoteConfirmation: true, // ... and get a confirmation mail when the owner can be the reply-to
        },
        options: {
            optionTypes: ['date_willJoin', 'text_number'],
            showOptionTypeIcon: true,
            showDescription: true,
            showMaxParticipants: true,
        },
    },],
    [TemplateKey.participationNew, {
        general: {
            inheritFrom: TemplateKey.participation,
            useStepper: true
        },
        defaultSettings: {
            theme: 'people-register',
            showParticipants: true,
            askForEmailAdress: true,
        },
    },],
    [TemplateKey.duties, {
        general: {
            inheritFrom: TemplateKey.advanced,
            previewImage: '/poll-preview/task-checklist.webp',
            icon: 'checklist',
            useOptionsListPreview: false,

        },
        availableSettings: {
            sendVoteConfirmation: false, // participant confirmation mail only for advanced/participation/invitation/potluck
            registerPeople: false,
            registerPeopleAdvanced: false,
            chooseTheme: true,
            location: false,
            // no meeting link at duties - the tasks happen on site (C2, Kai 2026-11-02)
            meetingLinkFirstStep: false,
            meetingLink: false,
            locationAdvanced: true,
            meetingLinkAdvanced: false,
            when: false,
            whenAdvanced: false,
            respondTillAdvanced: true,
            formToUseAdvanced: false
        },
        defaultSettings: {
            allowToAddOptions: true,
            theme: 'duties',
            showParticipants: true,
        },
        options: {
            optionTypes: ['date', 'text'],
            showDescription: true,
            showMaxParticipants: true,
            showOptionTypeIcon: false,
        },
    },],
    [TemplateKey.dutiesDate, {
        general: {
            inheritFrom: TemplateKey.duties,
        },
    }],
    [TemplateKey.dutiesNew, {
        general: {
            inheritFrom: TemplateKey.duties,
            useStepper: true
        },
        defaultSettings: {
            allowToAddOptions: true,
            theme: 'duties',
            showParticipants: true,
        },
    },],
    [TemplateKey.preference, {
        general: {
            inheritFrom: TemplateKey.advanced,
            icon: 'poll',
            previewImage: '/poll-preview/online-poll.webp',
            useOptionsListPreview: false,
        },
        availableSettings: {
            sendVoteConfirmation: false, // participant confirmation mail only for advanced/participation/invitation/potluck
            registerPeople: false,
            registerPeopleAdvanced: false,
            chooseTheme: true,
            location: false,
            meetingLink: false,
            locationAdvanced: false,
            meetingLinkAdvanced: false,
            when: false,
            whenAdvanced: false,
            respondTill: true, // votes and elections: the closing date belongs to the first section (decision Kai 2026-10-09)
            respondTillAdvanced: false,
            allowToAddOptions: false,
            askForName: true,
            formToUseAdvanced: false
        },
        defaultSettings: {
            showParticipants: false,
            theme: 'human-hands',
            askForName: false,
        },
        options: {
            optionTypes: ['text_slider', 'checkboxes', 'radio', 'dropdown', 'text_text'],
            showOptionTypeIcon: true,
            showDescription: false,
            showMaxParticipants: false,
        },
    }],
    [TemplateKey.preferenceNew, {
        general: {
            inheritFrom: TemplateKey.preference,
            useStepper: true,
        },
        defaultSettings: {
            showParticipants: false,
            theme: 'human-hands',
        }
    },],

    [TemplateKey.order, {
        general: {
            inheritFrom: TemplateKey.advanced,
            icon: 'shoping_cart',
            previewImage: '/poll-preview/group-order.webp',
            defaultOptions: 'order',
            useOptionsListPreview: false,
        },
        availableSettings: {
            sendVoteConfirmation: false, // participant confirmation mail only for advanced/participation/invitation/potluck
            chooseTheme: true,
            registerPeople: false,
            registerPeopleAdvanced: false,
            location: false,
            meetingLink: false,
            locationAdvanced: false,
            meetingLinkAdvanced: false,
            when: false,
            whenAdvanced: false,
            respondTillAdvanced: false,
            allowToAddOptions: false,
            formToUseAdvanced: false
        },
        defaultSettings: {
            theme: 'pizza',
            allowToAddOptions: false,
            registerPeople: false,
            askForMultiplePersons: false,
            askForKids: false,
            askForPrivateComments: false,
            showParticipants: true,
        },
        options: {
            optionTypes: ['text_textAndNumber'],
            showDescription: false,
            showMaxParticipants: false,
            showOptionTypeIcon: false,
        },
        responsesDashboard: {
            showChartBestTextOptions: false,
        }
    }],
    [TemplateKey.lunchGroup, {
        general: {
            inheritFrom: TemplateKey.advanced,
            previewImage: '/poll-preview/lunch-group.webp',
            useOptionsListPreview: true,
            defaultOptions: 'lunch'
        },
        availableSettings: {
            sendVoteConfirmation: false, // participant confirmation mail only for advanced/participation/invitation/potluck
            chooseTheme: true,
            registerPeople: false,
            registerPeopleAdvanced: false,
            location: false,
            meetingLink: false,
            locationAdvanced: false,
            meetingLinkAdvanced: false,
            when: false,
            whenAdvanced: false,
            respondTillAdvanced: false,
            allowToAddOptions: false,
            formToUseAdvanced: false
        },
        defaultSettings: {
            theme: 'spaghetti',
            allowToAddOptions: false,
            registerPeople: false,
            askForMultiplePersons: false,
            askForKids: false,
            showParticipants: true,
        },
        options: {
            optionTypes: ['date_willJoin', 'text', 'text_text'],
            showDescription: false,
            showMaxParticipants: false,
            showOptionTypeIcon: false,
        },
        responsesDashboard: {
            showChartBestTextOptions: false,
        }
    }],
    [TemplateKey.doodleAlternative, {
        general: {
            inheritFrom: TemplateKey.schedule,
            previewImage: '/poll-preview/doodle-poll.webp',
            icon: 'date_range',
        }
    },],
    [TemplateKey.doodle, {
        general: {
            inheritFrom: TemplateKey.schedule,
            previewImage: '/poll-preview/doodle-poll.webp',
            formToUse: 'matrix',
        },
        availableSettings: {
            showParticipants: false,
            allowToAddOptions: false,
        },
        defaultSettings: {
            theme: 'no-theme',
            showParticipants: true,
            mayBe: true,
        },
        options: {
            showDescription: true,
            optionTypes: ['date', 'text'],
            showOptionTypeIcon: false,
        },
    },],
    [TemplateKey.doodleAlternativeNew, {
        general: {
            inheritFrom: TemplateKey.doodleAlternative,
        },
        defaultSettings: {
            theme: 'no-theme',
            showParticipants: true,
            mayBe: true,
        },
    },],
    [TemplateKey.advanced, {
        general: {
            icon: 'settings',
            previewImage: '/poll-preview/settings.webp',
            formToUse: 'genericWithTheme',
            defaultOptions: '',
            useStepper: true,
            useOptionsListPreview: true,
        },
        availableSettings: {
            advanced: true,
            locationFirstStep: false,
            meetingLinkFirstStep: false,
            location: false,
            meetingLink: false,
            when: false,
            pollDescription: true,
            longDescription: true,
            dateRelated: false,
            locationAdvanced: true,
            meetingLinkAdvanced: true,
            whenAdvanced: true,
            timeZoneAdvanced: true,
            allowToAddOptions: true,
            allowToAddOptionsAdvanced: false,
            getNotifications: true,
            chooseTheme: true,
            registerPeople: false,
            registerPeopleAdvanced: true,
            maxPersons: true,
            respondTill: false,
            respondTillAdvanced: true,
            askForEmailAdress: true,
            sendVoteConfirmation: true,
            askForPhoneNumber: true,
            askForName: false,
            privateComments: true,
            showParticipants: true,
            askForPublicComments: true,
            allowParticipantsNotifications: true,
            mayBe: false,
            allowMayBeAdvanced: true,
            formToUseAdvanced: true
        },
        options: {
            skipOptionsStep: false,
            showAddDate: true,
            showDescription: true,
            showMaxParticipants: true,
            optionTypes: ['date', 'date_willJoin', 'divider', 'text_text', 'text_slider', 'text_number', 'textAndNumber_textAndNumber', 'text', 'checkboxes', 'radio', 'dropdown'],
            showOptionTypeIcon: true,
        },
        defaultSettings: {
            theme: 'no-theme',
            showAdvancedSettings: true,
            showParticipants: true,
        },
        responsesDashboard: {
            showChartNumberOfReponses: true,
            showDaysSinceCreation: true,
            showChartBestDateOptions: true,
            showChartBestTextOptions: true,
            showChartRegisterPeople: true
        }
    },],
]);


